import React, { useEffect, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { RigidBody, RapierRigidBody, CapsuleCollider } from "@react-three/rapier";
import * as THREE from "three";
import {
  controlsState,
  mouseState,
  yawToForward,
  getOrbitCameraOffset,
  playerPosition,
} from "./controls";
import { getSkinTexture, getShirtTexture, getPantsTexture } from "./textures";
import { playFootstep, playJump } from "./audio";

const MAX_SPEED = 5.2;
const MAX_REVERSE_SPEED = 2.6;
const ACCEL = 16;
const DECEL = 14;
const TURN_RATE = 2.4;
const JUMP_SPEED = 6.5;

interface CharacterProps {
  spawnPosition?: [number, number, number];
  onPositionChange?: (position: THREE.Vector3) => void;
}

const Character: React.FC<CharacterProps> = ({
  spawnPosition = [0, 1.2, 0],
  onPositionChange,
}) => {
  const bodyRef = useRef<RapierRigidBody>(null);
  const meshGroupRef = useRef<THREE.Group>(null);
  const leftLegRef = useRef<THREE.Mesh>(null);
  const rightLegRef = useRef<THREE.Mesh>(null);
  const leftArmRef = useRef<THREE.Mesh>(null);
  const rightArmRef = useRef<THREE.Mesh>(null);
  const { camera } = useThree();
  const cameraCurrentPos = useRef(new THREE.Vector3(0, 4, 9));
  const cameraTarget = useRef(new THREE.Vector3());
  const currentSpeed = useRef(0);
  const walkCycle = useRef(0);
  const footstepTimer = useRef(0);
  const isGrounded = useRef(true);
  const jumpCooldown = useRef(0);
  const initialSpawnRef = useRef(spawnPosition);

  const skin = getSkinTexture();
  const shirt = getShirtTexture();
  const pants = getPantsTexture();

  // Force the correct spawn position once the physics body exists — the
  // declarative `position` prop can be unreliable across React StrictMode's
  // dev-only double-mount of physics bodies.
  useEffect(() => {
    bodyRef.current?.setTranslation(
      { x: spawnPosition[0], y: spawnPosition[1], z: spawnPosition[2] },
      true,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useFrame((_, rawDelta) => {
    const body = bodyRef.current;
    if (!body) return;
    const delta = Math.min(rawDelta, 1 / 30);

    const turnInput = (controlsState.left ? 1 : 0) - (controlsState.right ? 1 : 0);
    if (turnInput !== 0) mouseState.orbitYaw += turnInput * TURN_RATE * delta;

    const yaw = mouseState.orbitYaw;
    const forwardVec = yawToForward(yaw);

    let targetSpeed = 0;
    if (controlsState.forward || mouseState.rightDown) targetSpeed = MAX_SPEED;
    else if (controlsState.backward) targetSpeed = -MAX_REVERSE_SPEED;
    if (controlsState.brake) targetSpeed = 0;

    const rate = controlsState.brake
      ? DECEL * 2
      : Math.abs(targetSpeed) > Math.abs(currentSpeed.current)
        ? ACCEL
        : DECEL;
    currentSpeed.current = THREE.MathUtils.damp(currentSpeed.current, targetSpeed, rate, delta);
    if (Math.abs(currentSpeed.current) < 0.01) currentSpeed.current = 0;

    const linvel = body.linvel();
    isGrounded.current = Math.abs(linvel.y) < 0.25;
    if (jumpCooldown.current > 0) jumpCooldown.current -= delta;

    let verticalVel = linvel.y;
    if (controlsState.jump) {
      controlsState.jump = false; // edge-triggered — consume immediately
      if (isGrounded.current && jumpCooldown.current <= 0) {
        verticalVel = JUMP_SPEED;
        jumpCooldown.current = 0.5;
        playJump();
      }
    }

    const desired = forwardVec.clone().multiplyScalar(currentSpeed.current);
    body.setLinvel({ x: desired.x, y: verticalVel, z: desired.z }, true);

    if (meshGroupRef.current) meshGroupRef.current.rotation.y = yaw;

    // Walk-cycle animation, speed-scaled
    const speedFrac = Math.abs(currentSpeed.current) / MAX_SPEED;
    walkCycle.current += delta * 8 * (0.4 + speedFrac);
    const swing = Math.sin(walkCycle.current) * 0.55 * speedFrac;
    if (leftLegRef.current) leftLegRef.current.rotation.x = swing;
    if (rightLegRef.current) rightLegRef.current.rotation.x = -swing;
    if (leftArmRef.current) leftArmRef.current.rotation.x = -swing;
    if (rightArmRef.current) rightArmRef.current.rotation.x = swing;

    // Footstep sound while walking on the ground
    if (isGrounded.current && speedFrac > 0.15) {
      footstepTimer.current -= delta;
      if (footstepTimer.current <= 0) {
        playFootstep();
        footstepTimer.current = 0.38 / Math.max(speedFrac, 0.35);
      }
    } else {
      footstepTimer.current = 0;
    }

    const position = body.translation();
    let pos = new THREE.Vector3(position.x, position.y, position.z);

    // Safety net — teleport back if somehow knocked off the walkable area
    if (pos.y < -5) {
      const [sx, sy, sz] = initialSpawnRef.current;
      const safeY = Math.max(sy, 1.5);
      body.setTranslation({ x: sx, y: safeY, z: sz }, true);
      body.setLinvel({ x: 0, y: 0, z: 0 }, true);
      pos = new THREE.Vector3(sx, safeY, sz);
    }
    onPositionChange?.(pos);
    playerPosition.copy(pos);

    // Orbit camera — follows mouse yaw/pitch around the character
    const offset = getOrbitCameraOffset(yaw, mouseState.orbitPitchT);
    const idealPos = pos.clone().add(offset);
    cameraCurrentPos.current.lerp(idealPos, 1 - Math.pow(0.001, delta));
    camera.position.copy(cameraCurrentPos.current);

    const lookAt = pos.clone();
    lookAt.y += 0.9;
    cameraTarget.current.lerp(lookAt, 1 - Math.pow(0.001, delta));
    camera.lookAt(cameraTarget.current);
  });

  return (
    <RigidBody
      ref={bodyRef}
      position={spawnPosition}
      colliders={false}
      linearDamping={0.5}
      angularDamping={2}
      enabledRotations={[false, false, false]}
      mass={1}
    >
      <CapsuleCollider args={[0.35, 0.32]} position={[0, 0.67, 0]} />
      <group ref={meshGroupRef}>
        {/* Head */}
        <mesh position={[0, 1.35, 0]} castShadow>
          <boxGeometry args={[0.34, 0.34, 0.34]} />
          <meshStandardMaterial map={skin} roughness={0.9} />
        </mesh>
        {/* Torso */}
        <mesh position={[0, 0.95, 0]} castShadow>
          <boxGeometry args={[0.46, 0.5, 0.28]} />
          <meshStandardMaterial map={shirt} roughness={1} />
        </mesh>
        {/* Arms */}
        <mesh ref={leftArmRef} position={[-0.32, 0.98, 0]} castShadow>
          <boxGeometry args={[0.14, 0.5, 0.14]} />
          <meshStandardMaterial map={skin} roughness={0.9} />
        </mesh>
        <mesh ref={rightArmRef} position={[0.32, 0.98, 0]} castShadow>
          <boxGeometry args={[0.14, 0.5, 0.14]} />
          <meshStandardMaterial map={skin} roughness={0.9} />
        </mesh>
        {/* Legs */}
        <mesh ref={leftLegRef} position={[-0.13, 0.4, 0]} castShadow>
          <boxGeometry args={[0.17, 0.55, 0.17]} />
          <meshStandardMaterial map={pants} roughness={1} />
        </mesh>
        <mesh ref={rightLegRef} position={[0.13, 0.4, 0]} castShadow>
          <boxGeometry args={[0.17, 0.55, 0.17]} />
          <meshStandardMaterial map={pants} roughness={1} />
        </mesh>
      </group>
    </RigidBody>
  );
};

export default Character;
